import { useEffect, useMemo, useState } from "react";
import { Page } from "../../../components/page-base";
import { DatagridResponsive } from "../../../components/datagrid-base";
import { useSearchParams } from "react-router-dom";
import { TDatagridData, TDGKey } from "../../../components/datagrid-base/types";
import { Input } from "../../../components/inputs";
import { useForm } from "react-hook-form";
import {
  AccountSelectContainer,
  HomeForm,
  UserContainer,
  SubmitBtn,
  DgContainer
} from "./styles";
import { Add, ViewListOutlined } from "@mui/icons-material";
import isMobile from "is-mobile";
import { toast } from "react-toastify";
import { useAuthContext, useNavigationContext } from "../../../contexts";
import theme from "../../../styles/theme";
import { AccountsService } from "../../accounts/services";
import { TAccount, TAccountRes } from "../../accounts/models";
import permissionsStore from "../../../store/permissions";
import { TSelectAccounts } from "../../scenarios/models";
import { UserService } from "../services";
import { TPaymentType, TRole, TUser } from "../models";
import { FormRow } from "../../../components/datagrid-base/components/container/styles";
import { useDebounce } from "@/utils/useDebounce";
import RecentActorsOutlinedIcon from "@mui/icons-material/RecentActorsOutlined";

export const UsersModule: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthContext();
  const { role } = permissionsStore();
  const {
    selectedAccountCombo,
    setSelectedAccountCombo,
    setRowsPerPage,
    rowsPerPage
  } = useNavigationContext();

  const busca = useMemo(() => {
    return searchParams.get("busca") || "";
  }, [searchParams]);

  const options = [
    {
      label: "Administrador",
      description: "Poderá acessar todas as funcionalidades do sistema",
      value: "admin"
    },
    {
      label: "Colaborador",
      description: "Poderá acessar apenas as funcionalidades de análise",
      value: "collaborator"
    }
  ];

  const { debounce } = useDebounce();
  const [count, setCount] = useState<number>(0);
  const [users, setUsers] = useState<TUser[]>([]);
  const [tab, setTab] = useState<number>(1);
  const [accountsOptions, setAccountOptions] = useState<TSelectAccounts[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  const fetchData = async () => {
    try {
      const usersRes = await UserService.getUsers(
        selectedAccountCombo!,
        page,
        busca,
        rowsPerPage
      );

      if (usersRes.success) {
        if (usersRes.data.length === 0 && page > 1) {
          navigateTable("1");
        }
        if (page > 1) {
          setUsers((prev) => [...prev, ...usersRes.data]);
        } else {
          setUsers(usersRes.data);
        }
        setCount(usersRes.meta?.total || 0);
      } else {
        setUsers([]);
        setCount(0);
        toast.error("Erro ao consultar usuários");
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setUsers([]);
      setCount(0);
      toast.error("Erro ao consultar usuários");
    }
  };

  useEffect(() => {
    setRowsPerPage(10);
    navigateTable("1");
    /* eslint-disable-next-line */
  }, [selectedAccountCombo]);

  useEffect(() => {
    if (selectedAccountCombo) {
      fetchData();
    }
    /* eslint-disable-next-line */
  }, [page, selectedAccountCombo, rowsPerPage, busca]);

  const fetchAllAccounts = async () => {
    const allAccounts: TAccount[] = [];
    let page = 1;
    let total = 0;

    try {
      do {
        const response: TAccountRes | null = await AccountsService.getAccounts(
          page,
          "",
          100
        );
        if (response && response.data && Array.isArray(response.data)) {
          allAccounts.push(...response.data);
        }
        if (response && response.meta) {
          total = response.meta.total;
        } else {
          total = 0;
        }
        page++;
      } while (allAccounts.length < total);
      if (allAccounts.length) {
        if (selectedAccountCombo) {
          const selected = allAccounts.find(
            (acc) => acc.id === selectedAccountCombo
          );
          setSelectedAccountCombo(selected!.id);
        } else {
          const oldestAccount = allAccounts.reduce((oldest, current) => {
            return new Date(current.createdAt) < new Date(oldest.createdAt)
              ? current
              : oldest;
          });
          setSelectedAccountCombo(oldestAccount.id);
        }
        const options = allAccounts.map((acc) => ({
          value: acc.id.toString(),
          label: acc.name
        }));
        setAccountOptions(options);
      }
    } catch (error) {
      console.error("Erro ao buscar contas:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchAllAccounts();
    /* eslint-disable-next-line */
  }, [user]);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      email: "",
      fullName: "",
      role: "collaborator",
      id: 1
    }
  });

  const onSubmit = async (payload: TDatagridData) => {
    const { email, fullName, role } = payload;
    if (isEdit) {
      const r = await UserService.editUser(
        fullName,
        selectedAccountCombo!,
        watch("id"),
        role
      );
      if (r?.success) {
        fetchData();
        toast.success(r.message);
        setTab(1);
        resetForm();
      } else {
        toast.error(r);
      }
    } else {
      const r = await UserService.createUser(
        fullName,
        role,
        email,
        selectedAccountCombo!
      );

      if (r?.success) {
        fetchData();
        toast.success(r.message);
        setTab(1);
        resetForm();
      } else {
        toast.error(r?.message);
      }
    }
  };

  const resetForm = () => {
    const formData = {
      fullName: "",
      email: "",
      role: "collaborator",
      id: 0
    };
    reset(formData, {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false
    });
  };

  const navigateTable = (newPage: string) => {
    setPage(Number(newPage));
  };

  const handleDelete = (userParam: TDGKey) => {
    setValue("id", userParam.id, {
      shouldValidate: true,
      shouldDirty: true
    });
    if (selectedAccountCombo) {
      deleteUser(selectedAccountCombo);
    } else {
      toast.error("Selecione uma conta para excluir o usuário.");
    }
  };

  const deleteUser = async (accountId: number) => {
    const response = await UserService.deleteUser(accountId, watch("id"));
    if (response?.success) {
      fetchData();
      toast.success(response.message);
      resetForm();
      setTab(1);
    } else {
      toast.error(response);
    }
  };

  const handleEdit = (userParam: TDGKey) => {
    const rules = { shouldValidate: true, shouldDirty: true };
    setValue("fullName", userParam.text ?? "", { ...rules });
    setValue("email", userParam.text1 ?? "", { ...rules });
    setValue("role", deParseRole(userParam.text3 ?? ""), { ...rules });
    setValue("id", userParam.id, { ...rules });
    setIsEdit(true);
    setTab(2);
  };

  const deParseRole = (role: string) => {
    if (role === "Proprietário") {
      return "owner";
    } else if (role === "Administrador") {
      return "admin";
    } else {
      return "collaborator";
    }
  };

  const parseRole = (role: TRole) => {
    if (role === "owner") {
      return "Proprietário";
    } else if (role === "admin") {
      return "Administrador";
    } else {
      return "Colaborador";
    }
  };

  const parsePaymentType = (paymentType: TPaymentType) => {
    if (paymentType === "PREPAID") {
      return "Pré-pago";
    } else {
      return "Pós-pago";
    }
  };

  const createRow = (users: TUser[]) => {
    const rowItem: TDatagridData[] = [];
    if (role !== "owner") {
      users = users.filter((user) => user.role !== "owner");
    }
    users.forEach((s) => {
      rowItem.push({
        text: s.fullName,
        text1: s.email,
        text2: parsePaymentType(s.paymentType),
        text3: parseRole(s.role),
        id: s.id
      });
    });

    return rowItem;
  };

  const onCancel = () => {
    toast.warn("Alterações descartadas.");
    setIsEdit(false);
    resetForm();
    setTab(1);
  };

  return (
    <Page.Root>
      <Page.Header
        title="Usuários"
        key="users"
        iconPage={<RecentActorsOutlinedIcon />}
        controlElement={[
          !isMobile() && role !== "collaborator" && (
            <AccountSelectContainer key="account-select">
              <span className="label">Conta selecionada:</span>
              <Input.Select
                onChange={(newValue) => {
                  setSelectedAccountCombo(Number(newValue));
                }}
                selectedValue={selectedAccountCombo?.toString()}
                options={accountsOptions}
              />
            </AccountSelectContainer>
          ),

          !isMobile() && (
            <Page.Search
              key={"search-account"}
              placeholder={"Busca por nome ou email"}
              changeTextSearch={(texto) =>
                debounce(() => {
                  setSearchParams(
                    { busca: texto, pagina: "1" },
                    { replace: true }
                  );
                })
              }
            />
          ),
          <div style={{ width: "inherit" }} key="page-button">
            {role !== "collaborator" && (
              <Page.Button
                label={
                  isMobile()
                    ? ""
                    : tab === 1
                    ? "Criar Usuário"
                    : "Lista de Usuários"
                }
                borderColor={theme.colors.palete.gold}
                disabled={false}
                startIcon={tab === 1 ? <Add /> : <ViewListOutlined />}
                onClick={() => {
                  if (tab === 1) {
                    setTab(2);
                  } else {
                    resetForm();
                    setTab(1);
                  }
                }}
              />
            )}
          </div>
        ].filter(Boolean)}
      />

      {tab == 1 ? (
        <>
          <UserContainer>
            {isMobile() && (
              <AccountSelectContainer key="account-select">
                <span className="label">Conta selecionada:</span>
                <Input.Select
                  onChange={(newValue) => {
                    setSelectedAccountCombo(Number(newValue));
                  }}
                  selectedValue={selectedAccountCombo?.toString()}
                  options={accountsOptions}
                />
              </AccountSelectContainer>
            )}
            <DgContainer>
              <DatagridResponsive.Combo
                headers={[
                  { field: "Nome", width: "15vw" },
                  { field: "Email", width: "15vw" },
                  { field: "Tipo de pagamento", width: "10vw" },
                  { field: "Nível de acesso", width: "10vw" },
                  ...(role !== "collaborator"
                    ? [{ field: "Ações", width: "10vw" }]
                    : [])
                ]}
                count={count}
                handleEdit={role !== "collaborator" ? handleEdit : undefined}
                handleDelete={
                  role !== "collaborator"
                    ? {
                        onDelete: handleDelete,
                        modalTitle: "Excluir usuário",
                        modalText:
                          "Após a exclusão o usuário não poderá mais acessar o sistema."
                      }
                    : undefined
                }
                data={createRow(users)}
                navigateTable={navigateTable}
                rowHeader="text"
                page={page}
              />
            </DgContainer>
          </UserContainer>
        </>
      ) : (
        <>
          <HomeForm>
            <h2 className="title">
              {isEdit ? "Editar usuário" : "Cadastro de usuário"}
            </h2>
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <FormRow>
                <Input.FormLabel
                  name="fullName"
                  control={control}
                  label="Nome completo"
                  placeholder="Informe o nome do usuário"
                  rules={{ required: "O nome é obrigatório" }}
                />
                {!isEdit && (
                  <Input.FormLabel
                    name="email"
                    control={control}
                    label="Email"
                    placeholder="informe o email do usuário"
                    rules={{
                      required: "O preenchimento do campo é obrigatório"
                    }}
                  />
                )}
              </FormRow>
              {watch("role") != "owner" && (
                <FormRow style={{ marginTop: "5vh" }}>
                  <Input.FormRadio
                    name="role"
                    control={control}
                    options={options}
                    rules={{ required: "Por favor, selecione uma opção." }}
                  />
                </FormRow>
              )}

              <div className="btns-container">
                <SubmitBtn type="submit" disabled={watch("fullName") == ""}>
                  Salvar
                </SubmitBtn>
                <SubmitBtn type="reset" disabled={false} onClick={onCancel}>
                  Cancelar
                </SubmitBtn>
              </div>
            </form>
          </HomeForm>
        </>
      )}
    </Page.Root>
  );
};
